/**
 * OKS-16 — small CSV helpers used by the bulk-import surface.
 *
 * Design constraints:
 *   - No CSV library on the wire — these helpers run in the browser
 *     before the file is shipped to Rails. The server (`Candidates::ImportService`)
 *     is the source of truth for parsing; the client only does cheap
 *     pre-flight checks to fail fast on the obvious cases the server
 *     would reject anyway.
 *   - `countDataRows` must NOT load the entire file into memory as a
 *     single string when the user picks a 5MB CSV. We stream the
 *     `Blob` via `text()` (which still buffers, but in a single
 *     pass and only when called) and split on common newline forms.
 *     For 500-row files this is well under any browser limit; the
 *     500-row cap itself is the natural ceiling.
 *   - `toCsvBlob` is used by the result panel's "Download skipped
 *     rows" affordance. We quote any cell that contains `,`, `"`,
 *     `\n`, or `\r`, and double up embedded quotes per RFC 4180.
 */

/**
 * Count CSV data rows (rows after the header), tolerating trailing
 * blank lines and bare-LF / CRLF line endings.
 *
 * Returns 0 when:
 *   - the file is empty
 *   - the file contains a single line (header only)
 *   - the file contains a header + only blank lines after it
 *
 * The returned count is a lower bound for "rows the server will try
 * to parse" — the server may reject malformed rows individually, but
 * the cap check needs only a count.
 *
 * **Why not parse?** Parsing CSV correctly with embedded newlines
 * inside quoted cells requires a full scanner; that lives on the
 * server. The realistic candidate-CSV (TikTok handles, names) won't
 * carry newlines inside cells, so a line-count gives a stable upper
 * bound for the cap-rejection UX. If a quoted-newline cell ever
 * sneaks in, the server count + per-row failure list still reports
 * the true outcome.
 */
export async function countDataRows(file: Blob): Promise<number> {
  const text = await file.text()
  if (text.length === 0) return 0
  // Normalise CRLF/CR to LF, then split. Strip the trailing empty
  // string from a final "\n" so files ending with a newline don't
  // count as having an extra blank row.
  const lines = text.replace(/\r\n?/g, "\n").split("\n")
  // Drop the header row. Any non-empty line below counts.
  const dataLines = lines.slice(1).filter((line) => line.trim().length > 0)
  return dataLines.length
}

/**
 * Build a CSV `Blob` from a header row + data rows. Used by the
 * skipped-rows downloader in `ImportResultPanel`.
 *
 * Cells are escaped per RFC 4180:
 *   - cells containing `,`, `"`, `\n`, or `\r` are wrapped in `"..."`;
 *   - embedded `"` is doubled (`""`).
 *
 * The returned `Blob` carries `text/csv;charset=utf-8` so the
 * browser's download UI surfaces the right MIME.
 */
export function toCsvBlob(headers: string[], rows: string[][]): Blob {
  const lines = [headers, ...rows].map((row) =>
    row.map(escapeCsvCell).join(","),
  )
  // RFC 4180 calls for CRLF; Excel + Numbers + Sheets all read LF
  // happily but CRLF avoids a UX wart on Windows Notepad opening.
  const body = lines.join("\r\n")
  return new Blob([body], { type: "text/csv;charset=utf-8" })
}

function escapeCsvCell(value: string): string {
  if (value.length === 0) return ""
  const needsQuoting = /[,"\r\n]/.test(value)
  if (!needsQuoting) return value
  return `"${value.replace(/"/g, '""')}"`
}

/**
 * Trigger a synthetic anchor click to download a `Blob` with the given
 * filename. Used by the "Download skipped rows" button in the result
 * panel — keeps the download path off the network (no Rails round-trip
 * to render a file the client already has in memory).
 *
 * Safe to call from event handlers in the browser. Tests mock this
 * indirectly via `URL.createObjectURL` stubs.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = filename
  // Firefox requires the anchor to be in the DOM before .click() fires.
  anchor.style.display = "none"
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  // Revoke on next tick — `click()` is sync but the download dialog
  // may still be reading the URL.
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

/**
 * Format a byte count for human display in the file picker (`12.4 KB`,
 * `2.1 MB`). Pure helper, no i18n — the unit suffix is universal.
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${kb.toFixed(1)} KB`
  const mb = kb / 1024
  return `${mb.toFixed(1)} MB`
}
